import { AGE_BANDS, BPAMetricsData, CycleMetric, MetricStatus } from './types';
import { BPA_SEVERITY_CONFIG, sortBPAProviders } from '../Charts/config';

export const emptyMetric = (): CycleMetric => ({
  count: 0,
  total_age_seconds: 0,
  bands: Object.fromEntries(
    AGE_BANDS.map(({ key }) => [key, 0]),
  ) as CycleMetric['bands'],
});

export const sumMetrics = (metrics: CycleMetric[]): CycleMetric =>
  metrics.reduce((total, metric) => {
    total.count += metric.count;
    total.total_age_seconds += metric.total_age_seconds;
    AGE_BANDS.forEach(({ key }) => {
      total.bands[key] += metric.bands[key];
    });
    return total;
  }, emptyMetric());

export const averageDays = (metric: CycleMetric) =>
  metric.count > 0 ? metric.total_age_seconds / metric.count / 86400 : 0;

export interface MetricsSeries {
  provider?: string;
  label: string;
  severities: CycleMetric[];
  selected: CycleMetric;
}

/** Keep counts, averages and provider identity together, including zero-age data. */
export const buildMetricsSeries = (
  source: BPAMetricsData,
  status: MetricStatus,
  providers: string[],
  severities: number[],
): MetricsSeries[] => {
  const cells = new Map(
    source.cells.map((cell) => [
      `${cell.provider}:${cell.severity}`,
      cell[status],
    ]),
  );
  const series = sortBPAProviders(source.providers)
    .filter(
      ({ provider }) => providers.length === 0 || providers.includes(provider),
    )
    .map(({ provider }) => {
      const values = BPA_SEVERITY_CONFIG.map(
        ({ value }) => cells.get(`${provider}:${value}`) ?? emptyMetric(),
      );
      return {
        provider,
        label: provider,
        severities: values,
        selected: sumMetrics(
          values.filter((_metric, index) =>
            severities.includes(BPA_SEVERITY_CONFIG[index].value),
          ),
        ),
      };
    })
    .filter((item) => item.selected.count > 0);
  if (series.length < 2) return series;
  return [
    {
      label: 'All',
      severities: BPA_SEVERITY_CONFIG.map((_severity, index) =>
        sumMetrics(series.map((item) => item.severities[index])),
      ),
      selected: sumMetrics(series.map((item) => item.selected)),
    },
    ...series,
  ];
};
